import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkRehype from 'remark-rehype';
import rehypeSanitize from 'rehype-sanitize';
import rehypeHighlight from 'rehype-highlight';
import rehypeMermaid from 'rehype-mermaid';
import rehypeKatex from 'rehype-katex';
import rehypeStringify from 'rehype-stringify';

const HTML_TAG_ALLOWLIST = new Set([
  'a',
  'abbr',
  'address',
  'article',
  'aside',
  'audio',
  'b',
  'bdi',
  'bdo',
  'blockquote',
  'br',
  'button',
  'canvas',
  'caption',
  'cite',
  'code',
  'col',
  'colgroup',
  'data',
  'datalist',
  'dd',
  'del',
  'details',
  'dfn',
  'dialog',
  'div',
  'dl',
  'dt',
  'em',
  'fieldset',
  'figcaption',
  'figure',
  'footer',
  'form',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'hr',
  'i',
  'iframe',
  'img',
  'input',
  'ins',
  'kbd',
  'label',
  'legend',
  'li',
  'link',
  'main',
  'map',
  'mark',
  'math',
  'menu',
  'meter',
  'nav',
  'noscript',
  'object',
  'ol',
  'optgroup',
  'option',
  'output',
  'p',
  'picture',
  'pre',
  'progress',
  'q',
  'rp',
  'rt',
  'ruby',
  's',
  'samp',
  'script',
  'section',
  'select',
  'slot',
  'small',
  'source',
  'span',
  'strong',
  'style',
  'sub',
  'summary',
  'sup',
  'svg',
  'table',
  'tbody',
  'td',
  'template',
  'textarea',
  'tfoot',
  'th',
  'thead',
  'time',
  'title',
  'tr',
  'track',
  'u',
  'ul',
  'var',
  'video',
  'wbr',
]);

const CUSTOM_TAG_REGEX = /<([A-Za-z0-9:_-]+)(\s[^>]*)?>([\s\S]*?)<\/\1\s*>/g;
const PLACEHOLDER_PREFIX = 'PROMPTBLOCK-';

function createProcessor() {
  return unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkMath)
    .use(remarkRehype)
    .use(rehypeSanitize)
    .use(rehypeKatex)
    .use(rehypeMermaid)
    .use(rehypeHighlight)
    .use(rehypeStringify);
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function generatePlaceholder() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${PLACEHOLDER_PREFIX}${crypto.randomUUID()}`;
  }

  return `${PLACEHOLDER_PREFIX}${Math.random()
    .toString(16)
    .slice(2)}${Date.now().toString(16)}`;
}

function extractCustomBlocks(text) {
  const blocks = [];

  const processed = text.replace(
    CUSTOM_TAG_REGEX,
    (match, tagName, rawAttributes = '', innerContent = '') => {
      const lowerTag = tagName.toLowerCase();
      if (HTML_TAG_ALLOWLIST.has(lowerTag)) {
        return match;
      }

      const placeholder = generatePlaceholder();
      const trimmedAttributes = rawAttributes.trimEnd();
      const openLabel = trimmedAttributes
        ? `<${tagName}${trimmedAttributes}>`
        : `<${tagName}>`;
      const closeLabel = `</${tagName}>`;

      blocks.push({
        placeholder,
        tagName,
        openLabel,
        closeLabel,
        content: innerContent,
      });

      return `\n\n${placeholder}\n\n`;
    },
  );

  return { processed, blocks };
}

function buildCustomBlockHtml(tagName, openLabel, closeLabel, innerHtml) {
  const safeTag = escapeHtml(tagName);
  const safeOpenLabel = escapeHtml(openLabel);
  const safeCloseLabel = escapeHtml(closeLabel);
  return (
    `<div class="prompt-xml-block" data-tag="${safeTag}">` +
    `<div class="prompt-xml-block__label prompt-xml-block__label--open">${safeOpenLabel}</div>` +
    `<div class="prompt-xml-block__body">${innerHtml}</div>` +
    `<div class="prompt-xml-block__label prompt-xml-block__label--close">${safeCloseLabel}</div>` +
    '</div>'
  );
}

function replacePlaceholders(html, block, renderedInnerHtml) {
  const blockHtml = buildCustomBlockHtml(
    block.tagName,
    block.openLabel,
    block.closeLabel,
    renderedInnerHtml,
  );
  const paragraphRegex = new RegExp(
    `<p>\\s*(?:${escapeRegExp(block.placeholder)})\\s*</p>`,
    'g',
  );

  let replaced = html.replace(paragraphRegex, blockHtml);
  if (replaced.includes(block.placeholder)) {
    replaced = replaced.split(block.placeholder).join(blockHtml);
  }

  return replaced;
}

async function renderInternal(content) {
  const { processed, blocks } = extractCustomBlocks(content);
  const processor = createProcessor();
  const file = await processor.process(processed);
  let html = String(file);

  for (const block of blocks) {
    const innerHtml = await renderInternal(block.content);
    html = replacePlaceholders(html, block, innerHtml);
  }

  return html;
}

export function createMarkdownRenderer() {
  return {
    async render(markdownContent) {
      if (!markdownContent) {
        return '';
      }

      return renderInternal(markdownContent);
    },
  };
}
